import { gql, graphQueryBang } from "shared/lib/graphql"

export interface AuthFlowStep {
  id: string
  name: string
  position: number
  step_type: string
  config: Record<string, unknown>
  needs_user_input: boolean
  input_method: "NONE" | "FORM" | "CONSENT_SCREEN"
}

export interface AuthFlowFormField {
  name: string
  prompt: string
  field_type: "TEXT" | "PASSWORD" | "URL"
  required: boolean
  protected: boolean
  default_value: string | null
  helper_text: string | null
}

export interface AuthFlowHttpRequestDetail {
  method: string
  url: string
  request_headers: Record<string, unknown> | null
  request_body: Record<string, unknown> | null
  response_status: number | null
  response_headers: Record<string, unknown> | null
  response_body: string | null
  duration_ms: number | null
  error: string | null
}

export interface AuthFlowStepExecuteResult {
  success: boolean
  output_context: Record<string, unknown> | null
  error_message: string | null
  redirect_url: string | null
  http_requests: AuthFlowHttpRequestDetail[] | null
}

const AuthFlowStepsQuery = gql`
  query AuthFlowStepsQuery($channel_module_name: String!) {
    auth_flow_steps(channel_module_name: $channel_module_name) {
      id
      name
      position
      step_type
      config
      needs_user_input
      input_method
    }
  }
`

const AuthFlowStepExecuteMutation = gql`
  mutation AuthFlowStepExecuteMutation(
    $step_id: ID!
    $execution_context: JSON
    $user_input: JSON
    $callback_params: JSON
  ) {
    authFlowStepExecute(
      input: {
        step_id: $step_id
        execution_context: $execution_context
        user_input: $user_input
        callback_params: $callback_params
      }
    ) {
      result {
        success
        output_context
        error_message
        redirect_url
        http_requests {
          method
          url
          request_headers
          request_body
          response_status
          response_headers
          response_body
          duration_ms
          error
        }
      }
    }
  }
`

export function getAuthFlowSteps(channelModuleName: string): Promise<AuthFlowStep[]> {
  return graphQueryBang(AuthFlowStepsQuery, {
    channel_module_name: channelModuleName,
  }).then(data => data.auth_flow_steps || [])
}

export function executeAuthFlowStep(
  stepId: string,
  options: {
    context?: Record<string, unknown>
    userInput?: Record<string, string>
    callbackParams?: Record<string, string>
  } = {}
): Promise<AuthFlowStepExecuteResult> {
  return graphQueryBang(AuthFlowStepExecuteMutation, {
    step_id: stepId,
    execution_context: options.context || null,
    user_input: options.userInput || null,
    callback_params: options.callbackParams || null,
  })
    .then(data => data.authFlowStepExecute.result)
    .catch(err => {
      if (err.details?.length > 0) {
        const messages = err.details.map((d: { message?: string }) => d.message).filter(Boolean).join("; ")
        throw new Error(messages || err.message)
      }
      throw err
    })
}
